'use client'

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdAdd, MdExitToApp, MdSearch, MdClose } from 'react-icons/md'
import {
    getDischarges,
    HospitalDischarge,
    DISCHARGE_STATUS_LABELS,
    DISCHARGE_STATUS_COLORS,
    DISCHARGE_TYPE_LABELS,
} from '@/lib/api/hospital'
import { HospitalSelector } from '@/components/HospitalSelector'
import { useHospitals } from '@/hooks/useHospitals'

export default function DischargePage() {
    const navigate = useNavigate()
    const [discharges, setDischarges] = useState<HospitalDischarge[]>([])
    const [filteredDischarges, setFilteredDischarges] = useState<
        HospitalDischarge[]
    >([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const {
        hospitals,
        loading: loadingHospitals,
        selectedHospitalId,
        setSelected,
    } = useHospitals()

    useEffect(() => {
        loadDischarges()
    }, [selectedHospitalId])

    useEffect(() => {
        if (searchTerm.trim()) {
            const filtered = discharges.filter(d =>
                d.client?.name.toLowerCase().includes(searchTerm.toLowerCase())
            )
            setFilteredDischarges(filtered)
        } else {
            setFilteredDischarges(discharges)
        }
    }, [searchTerm, discharges])

    const loadDischarges = async () => {
        setLoading(true)
        try {
            const response = await getDischarges(undefined, {
                hospitalId: selectedHospitalId || undefined,
            })
            if (response.success) {
                setDischarges(response.result)
                setFilteredDischarges(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar altas')
            }
        } catch (error) {
            toast.error('Erro ao carregar altas')
        } finally {
            setLoading(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando altas...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-lg">
                        <MdExitToApp className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Alta Hospitalar
                        </h1>
                        <p className="text-sm text-base-content/60">
                            Gerencie processo de alta dos pacientes
                        </p>
                    </div>
                </div>
                <button
                    className="btn btn-secondary gap-2"
                    onClick={() => navigate('/hospital/discharges/new')}
                >
                    <MdAdd className="text-xl" />
                    <span className="hidden sm:inline">Nova Alta</span>
                </button>
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4 flex flex-col gap-3">
                <HospitalSelector
                    hospitals={hospitals}
                    selectedId={selectedHospitalId}
                    onChange={setSelected}
                    loading={loadingHospitals}
                />
                <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por paciente..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </div>
            </div>

            {filteredDischarges.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdExitToApp className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            Nenhuma alta encontrada
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            Comece registrando uma alta hospitalar
                        </p>
                        <button
                            onClick={() => navigate('/hospital/discharges/new')}
                            className="btn btn-secondary gap-2"
                        >
                            <MdAdd className="text-xl" />
                            Nova alta
                        </button>
                    </div>
                </div>
            ) : (
                <div className="overflow-x-auto bg-base-100 rounded-xl border border-base-300/50">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Paciente</th>
                                <th>Unidade/Leito</th>
                                <th>Tipo</th>
                                <th>Data Solicitação</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDischarges.map(discharge => (
                                <tr
                                    key={discharge.id}
                                    className="hover cursor-pointer"
                                    onClick={() =>
                                        navigate(
                                            `/hospital/discharges/${discharge.id}`
                                        )
                                    }
                                >
                                    <td>
                                        <div className="font-bold">
                                            {discharge.client?.name}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="text-sm">
                                            {discharge.admission?.unit?.name}
                                            {discharge.admission?.bed && (
                                                <span className="text-base-content/60">
                                                    {' '}
                                                    - Leito{' '}
                                                    {
                                                        discharge.admission.bed
                                                            .number
                                                    }
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <span className="badge badge-ghost">
                                            {
                                                DISCHARGE_TYPE_LABELS[
                                                    discharge.dischargeType
                                                ]
                                            }
                                        </span>
                                    </td>
                                    <td>
                                        {new Date(
                                            discharge.requestedAt
                                        ).toLocaleDateString('pt-BR')}
                                    </td>
                                    <td>
                                        <span
                                            className={`badge ${DISCHARGE_STATUS_COLORS[discharge.status]}`}
                                        >
                                            {
                                                DISCHARGE_STATUS_LABELS[
                                                    discharge.status
                                                ]
                                            }
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}
